/**
 * Demand what-ifs: a product's unsaved month multipliers / unit overrides (or a trial daily rate)
 * re-forecast for that one product, without reloading the page. Pure helpers; the hook that runs
 * the simulations is useDemandWhatIf.
 */

import isEqual from "lodash/isEqual";
import { ForecastSimulation, ForecastWhatIf, ProductDemandSettings } from "services/InventoryService";
import { InventoryModifiers, ManualOverrides } from "./types";

/** Every product's adjustments, keyed by inventory id: what is on screen, or what is saved */
export type DemandSettingsByInventory = { byInventory: InventoryModifiers; manualOverrides: ManualOverrides };

export const EMPTY_DEMAND_SETTINGS: DemandSettingsByInventory = { byInventory: {}, manualOverrides: {} };

export const WHAT_IF_STATUS = { LOADING: "loading", READY: "ready", ERROR: "error" } as const;

/** One product row's simulation. While it recalculates, `result` still holds the previous answer. */
export type WhatIfSimulation = {
  status: typeof WHAT_IF_STATUS[keyof typeof WHAT_IF_STATUS];
  whatIf: ForecastWhatIf;
  result?: ForecastSimulation;
  error?: string;
};

export const productDemandSettings = (settings: DemandSettingsByInventory, inventoryId: string): ProductDemandSettings => ({
  salesModifiers: settings.byInventory[inventoryId] ?? {},
  manualOverrides: settings.manualOverrides[inventoryId] ?? {},
});

/** Products whose adjustments on screen differ from what is saved */
export function unsavedInventoryIds(current: DemandSettingsByInventory, saved: DemandSettingsByInventory): Set<string> {
  const inventoryIds = new Set([
    ...Object.keys(current.byInventory), ...Object.keys(current.manualOverrides),
    ...Object.keys(saved.byInventory), ...Object.keys(saved.manualOverrides),
  ]);
  return new Set(Array.from(inventoryIds).filter(id => !isEqual(productDemandSettings(current, id), productDemandSettings(saved, id))));
}

/** The what-if to forecast a product row with, or null when nothing about it is unsaved */
export function whatIfFor(
  grouping: { key: string; inventory_id: string },
  current: DemandSettingsByInventory,
  unsavedIds: Set<string>,
  trialRatesByKey: Record<string, number>,
): ForecastWhatIf | null {
  const hasUnsavedSettings = unsavedIds.has(grouping.inventory_id);
  const trialRate = trialRatesByKey[grouping.key];
  if (!hasUnsavedSettings && trialRate === undefined) return null;
  return {
    ...(hasUnsavedSettings ? productDemandSettings(current, grouping.inventory_id) : {}),
    ...(trialRate !== undefined ? { unitsPerDay: trialRate } : {}),
  };
}

/**
 * The /forecasts payload with some products' rows replaced by fresh forecasts (after a save), and
 * the risk summary re-filed to match: each product leaves the lists and rejoins under its new
 * severity, or stays off them.
 */
export function applyForecastUpdates(forecastsPayload: any, updates: Array<{ forecast: any; riskItem: any | null }>): any {
  const updatesByKey = new Map(updates.map(update => [update.forecast.key, update]));
  const riskLists: Record<string, any[]> = {};
  for (const [listName, items] of Object.entries<any[]>(forecastsPayload.riskSummary ?? {})) {
    riskLists[listName] = items.filter(item => !updatesByKey.has(item.groupingKey));
  }
  for (const { riskItem } of updates) {
    if (!riskItem) continue;
    const listName = RISK_LIST_BY_SEVERITY[riskItem.severity];
    if (!listName) throw new Error(`Unknown risk severity "${riskItem.severity}" for ${riskItem.groupingKey}`);
    riskLists[listName] = [...(riskLists[listName] ?? []), riskItem];
  }
  return {
    ...forecastsPayload,
    groupings: forecastsPayload.groupings.map((grouping: any) => updatesByKey.get(grouping.key)?.forecast ?? grouping),
    riskSummary: riskLists,
  };
}

// The server files risk items by severity under these /forecasts riskSummary lists
const RISK_LIST_BY_SEVERITY: Record<string, string> = { CRITICAL: "critical", "AT-RISK": "atRisk", WATCH: "watch" };
